import React, { useEffect } from 'react';
import './App.scss';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Home from './pages/Home';
import Registration from './pages/Registration';
import Dashboard from './pages/Dashboard';
import QrScanner from './pages/QrScanner';
import SelfieCapture from './pages/SelfieCapture';
import PrivateRoute from './components/PrivateRoute';
import Layout from './components/Layout';
import { useAppDispatch, useAppSelector } from './store/hooks';
import { setPreviousRoute } from './store/slices/navigationSlice';
import { useUserDataExpiration, useActivityTracker } from './utils/customHooks';
import { useDeviceDetection } from './hooks/useDeviceDetection';
import { applyAndroidPWAFixes, removeAndroidPWAFixes } from './utils/functions';


// Import exhibit pages
import ExhibitPage from './pages/ExhibitPage';
import LanguageSelection from './pages/LanguageSelection';
import PrivacyPolicy from './pages/PrivacyPolicy';
import ReadyToExplore from './pages/ReadyToExplore';
import LeaveYourMark from './pages/LeaveYourMark';
import ZonesDemo from './pages/ZonesDemo';

// Import orientation components
import OrientationLockDemo from './components/OrientationLockDemo';
import OrientationBlocker from './components/OrientationBlocker';

export default function App() {
  const { t } = useTranslation();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { language } = useAppSelector(state => state.user);
  const isLanguageSelected = language !== '';

  const { isMobile, isTablet } = useDeviceDetection();
  const shouldBlockLandscape = isMobile || isTablet;

  // Initialize user data expiration checking
  useUserDataExpiration();
  
  // Track user activity for expiration purposes
  useActivityTracker();

  // Apply Android PWA fixes on mount
  useEffect(() => {
    applyAndroidPWAFixes();
    
    // Cleanup on unmount
    return () => {
      removeAndroidPWAFixes();
    };
  }, []);

  // Keep track of navigation for back button functionality
  useEffect(() => {
    dispatch(setPreviousRoute(location.pathname));
  }, [location, dispatch]);

  return (
    <Layout>
      {/* Block landscape mode on mobile and tablet devices */}
      <OrientationBlocker 
        enabled={shouldBlockLandscape}
        message={t('common.orientationBlockerMessage')}
      />
      
      {/* Show orientation lock demo in development mode */}
      {/* {process.env.NODE_ENV === 'development' && (
        <OrientationLockDemo showDemo={true} enableAutoLock={false} />
      )} */}

      <Routes>
        {/* Root path redirects to language selection if language not selected */}
        <Route 
          path="/" 
          element={
            isLanguageSelected ? <Home /> : <Navigate to="/language" replace />
          } 
        />
        <Route path="/language" element={<LanguageSelection />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/register" element={<Registration />} />
        <Route path="/qr" element={<QrScanner />} />
        <Route path="/selfie" element={<SelfieCapture />} />
        <Route path="/leave-your-mark" element={<LeaveYourMark />} />
        <Route path="/ready-to-explore" element={<ReadyToExplore />} />
        <Route path="/dashboard" element={
          <PrivateRoute>
            <Dashboard />
          </PrivateRoute>
        } />

        {/* Exhibit routes */}
        <Route path="/exhibit/:exhibitId" element={<ExhibitPage />} />
        
        {/* Development/Testing routes */}
        {/* {process.env.NODE_ENV === 'development' && ( */}
          <Route path="/zones-demo" element={<ZonesDemo />} />
        {/* )} */}
      </Routes>
    </Layout>
  );
}
